import { Close, Edit, Save } from "@mui/icons-material";
import { IconButton, MenuItem, Select, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

import { GqlPlannerVisitStatus } from "~/__generated__";
import { StatusCheckmark } from "~/modules/app/components/StatusCheckmark";

import { ReviewVisitTableCell } from "../../../../components/ReviewVisitTable";
import { WeatherReportContentFragment } from "../WeatherReportContent/fragment.generated";

export type WeatherConditionValues = {
    startFormCondition: { conditionKey: string; optionKey: string } | null;
    submittedFormCondition: { conditionKey: string; optionKey: string } | null;
};

type ConditionItem = WeatherReportContentFragment["conditions"][number];

type WeatherReportFormValues = {
    startValue: string;
    endValue: string;
};

const toFormValue = (key: string | null | undefined): string => key ?? "";

interface WeatherReportTableRowProps {
    metric: string;
    condition: ConditionItem;
    visitStatus: GqlPlannerVisitStatus;
    onUpdate: (values: WeatherConditionValues) => Promise<void>;
}

export function WeatherReportTableRow({ metric, condition, visitStatus, onUpdate }: WeatherReportTableRowProps) {
    const [isEditing, setIsEditing] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const { control, handleSubmit, reset } = useForm<WeatherReportFormValues>({
        values: {
            startValue: toFormValue(condition.startKey),
            endValue: toFormValue(condition.endKey),
        },
    });

    const handleCancel = () => {
        reset();
        setIsEditing(false);
    };

    const handleFormSubmit: SubmitHandler<WeatherReportFormValues> = async values => {
        setIsLoading(true);
        await onUpdate({
            startFormCondition: condition.startFormConditionKey
                ? { conditionKey: condition.startFormConditionKey, optionKey: values.startValue }
                : null,
            submittedFormCondition: condition.endFormConditionKey
                ? { conditionKey: condition.endFormConditionKey, optionKey: values.endValue }
                : null,
        });
        setIsEditing(false);
        setIsLoading(false);
    };

    const canEdit =
        visitStatus !== GqlPlannerVisitStatus.Approved &&
        (condition.startFormOptions.length > 0 || condition.endFormOptions.length > 0);

    return (
        <>
            <ReviewVisitTableCell direction="row" gap={1} alignItems="center">
                <StatusCheckmark checked={condition.isValid} />
                <Typography variant="body2">{metric}</Typography>
            </ReviewVisitTableCell>

            <ReviewVisitTableCell>
                {isEditing ? (
                    <Controller
                        name="startValue"
                        control={control}
                        rules={{ required: !!condition.startFormConditionKey }}
                        render={({ field, fieldState }) => (
                            <Select
                                {...field}
                                size="small"
                                sx={{ minWidth: 80 }}
                                error={!!fieldState.error}
                                disabled={isLoading}
                            >
                                {condition.startFormOptions.map(option => (
                                    <MenuItem key={option.id} value={option.key}>
                                        {option.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        )}
                    />
                ) : (
                    <Typography variant="body2">{condition.startLabel ?? "—"}</Typography>
                )}
            </ReviewVisitTableCell>

            <ReviewVisitTableCell>
                {isEditing ? (
                    <Controller
                        name="endValue"
                        control={control}
                        rules={{ required: !!condition.endFormConditionKey }}
                        render={({ field, fieldState }) => (
                            <Select
                                {...field}
                                size="small"
                                sx={{ minWidth: 80 }}
                                error={!!fieldState.error}
                                disabled={isLoading}
                            >
                                {condition.endFormOptions.map(option => (
                                    <MenuItem key={option.id} value={option.key}>
                                        {option.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        )}
                    />
                ) : (
                    <Typography variant="body2">{condition.endLabel ?? "—"}</Typography>
                )}
            </ReviewVisitTableCell>

            <ReviewVisitTableCell>
                {canEdit &&
                    (isEditing ? (
                        <Stack direction="column">
                            <IconButton
                                size="small"
                                color="primary"
                                onClick={handleSubmit(handleFormSubmit)}
                                disabled={isLoading}
                            >
                                <Save />
                            </IconButton>
                            <IconButton size="small" onClick={handleCancel} disabled={isLoading}>
                                <Close fontSize="small" />
                            </IconButton>
                        </Stack>
                    ) : (
                        <IconButton size="small" onClick={() => setIsEditing(true)}>
                            <Edit fontSize="small" />
                        </IconButton>
                    ))}
            </ReviewVisitTableCell>
        </>
    );
}
